import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Crown, Zap, Users } from 'lucide-react';
import { usePractitionerSubscription } from '@/hooks/usePractitionerSubscription';

interface SubscriptionCardProps {
  compact?: boolean;
}

export const SubscriptionCard = ({ compact = false }: SubscriptionCardProps) => {
  const { 
    subscription, 
    plans, 
    isProPlan, 
    isBasicPlan,
    upgradeSubscription,
    isUpgrading 
  } = usePractitionerSubscription();

  const currentPlan = plans.find(p => p.name === subscription?.plan_name) || plans.find(p => p.name === 'Basic');
  const proPlan = plans.find(p => p.name === 'Pro');

  if (compact) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Subscription</CardTitle>
          {isProPlan ? <Crown className="h-4 w-4 text-accent" /> : <Zap className="h-4 w-4 text-muted-foreground" />}
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{subscription?.plan_name || 'Basic'}</div>
          <p className="text-xs text-muted-foreground">
            {isProPlan ? '$49/month' : 'Free forever'}
          </p>
          {isBasicPlan && (
            <Button 
              size="sm" 
              className="mt-2 w-full" 
              onClick={() => proPlan && upgradeSubscription(proPlan.id)}
              disabled={isUpgrading}
            >
              Upgrade to Pro
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Subscription Plans</h2>
        <p className="text-muted-foreground">Choose the plan that fits your practice</p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {plans.map((plan) => {
          const isCurrent = plan.name === (subscription?.plan_name || 'Basic');
          const isProPlanCard = plan.name === 'Pro';
          
          return (
            <Card key={plan.id} className={`relative ${isCurrent ? 'ring-2 ring-primary' : ''}`}>
              {isProPlanCard && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-gradient-primary text-primary-foreground">
                    <Crown className="h-3 w-3 mr-1" />
                    Most Popular
                  </Badge>
                </div>
              )}
              
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl">{plan.name}</CardTitle>
                  {isCurrent && <Badge variant="secondary">Current</Badge>}
                </div>
                <CardDescription>
                  <span className="text-3xl font-bold">
                    ${plan.price_monthly}
                  </span>
                  <span className="text-muted-foreground">/month</span>
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-4">
                {/* Prominent Group Limit Display */}
                <div className="p-4 bg-primary/10 rounded-lg border-2 border-primary/20">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-primary" />
                      <span className="font-semibold text-base">
                        {typeof plan.features?.max_groups === 'undefined'
                          ? 'Loading...'
                          : plan.features.max_groups === -1
                          ? 'Unlimited Groups'
                          : plan.features.max_groups === 1
                          ? '1 Group Only'
                          : `Up to ${plan.features.max_groups} Groups`}
                      </span>
                    </div>
                    {plan.features?.max_groups > 1 && plan.features.max_groups !== -1 && (
                      <Badge variant="secondary" className="font-bold">
                        {plan.features.max_groups}x
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center">
                    <Check className="h-4 w-4 text-primary mr-2" />
                    <span className="text-sm">{plan.features?.monthly_free_sessions || 0} free sessions/month</span>
                  </div>
                  <div className="flex items-center">
                    <Check className="h-4 w-4 text-primary mr-2" />
                    <span className="text-sm">{plan.features?.referral_rate ? (plan.features.referral_rate * 100).toFixed(0) : 0}% referral revenue share</span>
                  </div>
                  {plan.features.advanced_analytics && (
                    <div className="flex items-center">
                      <Check className="h-4 w-4 text-primary mr-2" />
                      <span className="text-sm">Advanced analytics & reports</span>
                    </div>
                  )}
                  {plan.features.branding_features && (
                    <div className="flex items-center">
                      <Check className="h-4 w-4 text-primary mr-2" />
                      <span className="text-sm">White-label branding</span>
                    </div>
                  )}
                  {plan.features.priority_support && (
                    <div className="flex items-center">
                      <Check className="h-4 w-4 text-primary mr-2" />
                      <span className="text-sm">Priority support & weekly payouts</span>
                    </div>
                  )}
                  {plan.features.community_access && (
                    <div className="flex items-center">
                      <Check className="h-4 w-4 text-primary mr-2" />
                      <span className="text-sm">Practitioner community access</span>
                    </div>
                  )}
                </div>

                {!isCurrent && (
                  <Button
                    className="w-full"
                    variant={isProPlanCard ? "default" : "outline"}
                    onClick={() => upgradeSubscription(plan.id)}
                    disabled={isUpgrading}
                  >
                    {isUpgrading ? 'Processing...' : 
                     isProPlanCard ? 'Upgrade to Pro' : 'Switch Plan'}
                  </Button>
                )}

                {isProPlanCard && (
                  <div className="mt-4 p-3 bg-muted rounded-lg">
                    <h4 className="font-medium text-sm mb-1">ROI Example</h4>
                    <p className="text-xs text-muted-foreground">
                      With 50 linked clients spending $11.16/month each:
                      <br />
                      Basic: $111.60/month (20% share)
                      <br />
                      <strong>Pro: $139.50/month (25% share)</strong>
                      <br />
                      Extra $27.90/month covers the $49 fee!
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};